import { FC, Fragment, SetStateAction } from 'react';
import { formatOrganisationName, formatSelectValue } from '../../utils';
import { Box, Flex, StyledFormSelect, StyledInput, StyledDatePicker, WrappedQuery } from '.';

interface FilterOption {
  label: string;
  value: string;
  isText?: boolean;
  isNumber?: boolean;
  isDate?: boolean;
  options?: { label: string; value: string | boolean }[];
  query?: { query: any; result: string; variables: any };
}

type Props = {
  changeHandler: (args: { value: string | Date | number | null }, stateKey?: string) => void;
  filterOptions: FilterOption[];
  filterState: SetStateAction<any>;
  searchName?: string;
  setSearchName?: (data: string) => void;
};

const DashboardFilters: FC<Props> = ({ changeHandler, filterOptions, filterState, searchName, setSearchName }) => {
  return (
    <Flex size="1" direction="row">
      {filterOptions.map((filterOption, index) => {
        return (
          <Fragment key={index}>
            {filterOption && filterOption.query && (
              <Flex size="0.25" direction="row" marginRight="10px">
                <span>{filterOption.label}</span>
                <WrappedQuery query={filterOption.query.query} variables={filterOption.query.variables}>
                  {({ loading, error, data, fetchMore }) => (
                    <StyledFormSelect
                      aria-label="Refine"
                      autoFocus={true}
                      inputValue={searchName}
                      isClearable={true}
                      name="refine"
                      mb="0"
                      onChange={option => changeHandler(option, filterOption.value)}
                      options={(() => {
                        if (loading || error) {
                          return [];
                        }

                        if (filterOption.query && data[filterOption.query.result].edges) {
                          return data[filterOption.query.result].edges.map((item: any) =>
                            formatSelectValue({
                              ...((item.node.firstName || item.node.lastName) && {
                                label: `${item.node.firstName} ${item.node.lastName}`,
                              }),
                              ...(item.node.localMunicipalityShorthand && {
                                label: item.node.localMunicipalityShorthand,
                              }),
                              ...((item.node.isFunder || item.node.isInstitution || item.node.isServiceProvider) && {
                                label: formatOrganisationName(item.node),
                              }),
                              ...(item.node.name && { label: item.node.name }),
                              value: item.node.id,
                            })
                          );
                        }

                        return (
                          filterOption.query &&
                          data[filterOption.query.result].map((item: any) =>
                            formatSelectValue({
                              ...((item.firstName || item.lastName) && { label: `${item.firstName} ${item.lastName}` }),
                              ...(item.localMunicipalityShorthand && { label: item.localMunicipalityShorthand }),
                              ...((item.isFunder || item.isInstitution || item.isServiceProvider) && {
                                label: formatOrganisationName(item.node),
                              }),
                              ...(item.name && { label: item.name }),
                              value: item.id,
                            })
                          )
                        );
                      })()}
                      placeholder="Select option"
                      setSearchName={setSearchName}
                      scrollHandler={async () => {
                        if (filterOption.query) {
                          // call the next page of the circuits query
                          return (
                            data[filterOption.query.result] &&
                            data[filterOption.query.result].pageInfo &&
                            data[filterOption.query.result].pageInfo.hasNextPage &&
                            fetchMore({
                              variables: {
                                input: {
                                  ...(filterOption.query.variables &&
                                    filterOption.query.variables.input && { ...filterOption.query.variables.input }),
                                  after:
                                    data[filterOption.query.result] &&
                                    data[filterOption.query.result].pageInfo &&
                                    data[filterOption.query.result].pageInfo.endCursor,
                                },
                              },
                              updateQuery: (previousResult, { fetchMoreResult }) => {
                                if (filterOption.query) {
                                  const newResults = fetchMoreResult[filterOption.query.result].edges;
                                  return {
                                    [filterOption.query.result]: {
                                      aggregate: fetchMoreResult[filterOption.query.result].aggregate,
                                      pageInfo: fetchMoreResult[filterOption.query.result].pageInfo,
                                      edges: [...previousResult[filterOption.query.result].edges, ...newResults],
                                      __typename: fetchMoreResult[filterOption.query.result].__typename,
                                    },
                                  };
                                }
                                return;
                              },
                            })
                          );
                        }
                        return false;
                      }}
                    />
                  )}
                </WrappedQuery>
              </Flex>
            )}

            {filterOption && filterOption.options && (
              <Flex size="0.25" direction="row" marginRight="10px">
                <span>{filterOption.label}</span>
                <StyledFormSelect
                  isClearable={true}
                  name="refine"
                  aria-label="Refine"
                  onChange={option => changeHandler(option, filterOption.value)}
                  options={filterOption.options}
                  placeholder="Select ..."
                  mb="0"
                />
              </Flex>
            )}

            {filterOption && (filterOption.isText || filterOption.isNumber) && (
              <Flex size="0.25" direction="row" marginRight="10px">
                <span>{filterOption.label}</span>
                <Box width="100%">
                  <StyledInput
                    type={filterOption.isNumber ? 'number' : 'text'}
                    id="searchTerm"
                    aria-label="Search"
                    onChange={(event: { target: { value: string | number } }) => {
                      changeHandler({ value: event.target.value }, filterOption.value);
                      const searchTerm =
                        event.target.value && event.target.value !== undefined ? event.target.value.toString() : '';
                      setSearchName && setSearchName(searchTerm);
                    }}
                    placeholder="Search..."
                    px={2}
                  />
                </Box>
              </Flex>
            )}

            {filterOption && filterOption.isDate && (
              <Flex size="0.25" direction="row" marginRight="10px">
                <span>{filterOption.label}</span>
                <Box width="100%">
                  <StyledDatePicker
                    aria-label="Date"
                    name="dateSearch"
                    isSingleDate={true}
                    setStartDate={(startDate: Date | null) => changeHandler({ value: startDate }, filterOption.value)}
                    setEndDate={() => null}
                    startDate={filterState[filterOption.value]}
                    px={2}
                  />
                </Box>
              </Flex>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
};

export default DashboardFilters;
